import { Component, Input, OnChanges } from '@angular/core';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { FuelcellChannelAddress } from '../../../shared/interfaces/fuelcell-data.interface';
import { FuelcellService } from '../../../shared/services/fuelcell.service';

/**
 * Settings of the fuel cell channel stored in the database (FG06 settings), currently
 * whether the message of the external fuel sensor is ignored. Nothing is sent to the device.
 * After saving the setting is read again; the section stays locked and greyed until this
 * succeeds, if necessary by a manual refresh. Shown as a sub-section of the cartridges
 * with its own refresh.
 */
@Component({
  selector: 'avs-fuelcell-sensor-settings',
  templateUrl: './fuelcell-sensor-settings.component.html',
  styleUrls: ['./fuelcell-sensor-settings.component.css']
})
export class FuelcellSensorSettingsComponent implements OnChanges {
  @Input() target?: FuelcellChannelAddress;

  /** Only a setting actually read may be edited and written back */
  public isLoaded: boolean = false;
  /** Editing stays locked from saving until the next successful read */
  public isBusy: boolean = false;
  /** Notice while saving and until the successful read */
  public busyMessage: string = '';
  /** Without a stored value the device does not support the setting */
  public isAvailable: boolean = false;
  public information: string = '';
  public ignoreExternalFuelSensor: boolean = false;
  private storedIgnoreExternalFuelSensor: boolean = false;
  /** The inputs are collapsed by default */
  public isSettingExpanded: boolean = false;

  constructor(
    private fuelcellService: FuelcellService,
    private alertService: AlertService
  ) { }

  public ngOnChanges(): void {
    this.load();
  }

  /** Current (stored) handling of the external fuel sensor for the header */
  public get statusText(): string {
    if (!this.isLoaded && !this.isBusy) { return 'k.A.'; }
    if (!this.isAvailable) { return 'nicht verfügbar'; }
    return this.storedIgnoreExternalFuelSensor ? 'wird ignoriert' : 'wird beachtet';
  }

  public get isChanged(): boolean {
    return this.ignoreExternalFuelSensor !== this.storedIgnoreExternalFuelSensor;
  }

  public get isEditable(): boolean {
    return this.isLoaded && !this.isBusy && this.isAvailable;
  }

  /** Only after reading it is certain that the device does not deliver the setting */
  public get isUnavailable(): boolean {
    return this.isLoaded && !this.isAvailable;
  }

  /** The previous value stays visible (greyed) until the new value is read; a successful read releases editing */
  public load(): void {
    this.isLoaded = false;
    this.information = '';
    if (!this.target) {
      this.apply(null);
      this.release();
      return;
    }

    this.fuelcellService.getSettings(this.target).subscribe({
      next: settings => {
        this.apply(settings.ignoreExternalFuelSensor);
        this.release();
        this.isLoaded = true;
      },
      // The error message itself is already shown by the HttpService.
      error: () => {
        this.apply(null);
        this.failRead('Die Einstellungen konnten nicht gelesen werden.');
      }
    });
  }

  /** Save: lock editing until the setting is read again successfully */
  public onSaveClicked(): void {
    if (!this.target || !this.isEditable || !this.isChanged) { return; }

    this.isBusy = true;
    this.busyMessage = 'Einstellung wird gespeichert …';
    this.fuelcellService.setIgnoreExternalFuelSensor(this.target, this.ignoreExternalFuelSensor).subscribe({
      next: () => {
        this.alertService.alert('Die Änderungen wurden erfolgreich gespeichert', AlertTypes.info);
        this.busyMessage = 'Einstellung gespeichert, wird neu gelesen …';
        this.load();
      },
      // After a failed save the stored setting is read again (the error itself is shown by the HttpService)
      error: () => this.load()
    });
  }

  private release(): void {
    this.isBusy = false;
    this.busyMessage = '';
  }

  /** After saving the section stays locked until a read succeeds; otherwise the read error is shown */
  private failRead(errorMessage: string): void {
    if (this.isBusy) {
      this.busyMessage = 'Die Daten konnten nicht neu gelesen werden. Zum Freigeben der Bearbeitung bitte manuell aktualisieren.';
    } else {
      this.information = errorMessage;
    }
  }

  private apply(ignoreExternalFuelSensor?: boolean | null): void {
    this.isAvailable = ignoreExternalFuelSensor != null;
    this.ignoreExternalFuelSensor = ignoreExternalFuelSensor ?? false;
    this.storedIgnoreExternalFuelSensor = this.ignoreExternalFuelSensor;
  }
}
